import LoadingScreen from "components/shared/LoadingScreen";
import KycDetails from "pages/kyc";
import { KycWrapper } from "pages/kyc/KycWrapper";
import { captureEvent, captureFailure } from "posthog";
import { FC, ReactNode, useCallback, useEffect } from "react";
import { Flow } from "types/flow";
import { KycRequirementForFeature, useConfig } from "./ConfigContext";
import { useUserContext } from "./UserContext";
import { AuthConsumer } from "./auth/use-auth";
import { EventLevel, sendEvent } from "utils/events/handler";

export type KycCheckerProps = {
    flow: Flow
    children: ReactNode
    presetKycCountry?: string
    fixedPresetKycCountry: boolean
    kycRequirements?: KycRequirementForFeature
    externalDigilocker?: boolean
}

export const KycChecker: FC<KycCheckerProps> = ({
    children,
    flow,
    presetKycCountry,
    fixedPresetKycCountry,
    kycRequirements,
    externalDigilocker
}) => {
    const { user, fetching, refreshUser } = useUserContext()
    const config = useConfig().config

    const kycVersion = config?.features?.use_old_kyc ? "old" : "new"

    const onStatusChange = useCallback((status: string) => {
        refreshUser()
        sendEvent("KYC_STATUS", EventLevel.INFO, { status });
        // bank step completion calls this without a status
        captureEvent("kyc_status_changed", { status: status ?? "BANK_VERIFIED", flow, kyc_version: kycVersion });
        // statuses from kycStatusToStep in constants.js
        if (status === "REJECTED" || status === "KICKED") {
            captureFailure("kyc_failed", { status, flow, kyc_version: kycVersion });
        }
    }, [flow, kycVersion])

    kycRequirements = kycRequirements ?? config?.kyc_requirements?.[flow]

    const isAllowedToTransact = Boolean(user) && user!.isKycVerified() && user!.isAllowedToTransact(
        kycRequirements?.phone,
        kycRequirements?.email,
        kycRequirements?.bank_account
    )
    const needsKyc = Boolean(user && config && !fetching && !isAllowedToTransact)

    useEffect(() => {
        if (needsKyc) {
            captureEvent("kyc_started", {
                flow,
                kyc_version: kycVersion,
                country: user?.getCountry()?.iso3 ?? presetKycCountry,
                kyc_verified: user?.isKycVerified(),
            });
        }
        // fire once when KYC becomes required
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [needsKyc])

    if (!user || !config || fetching) {
        return <LoadingScreen />
    }

    if (!isAllowedToTransact) {
        return (
            <AuthConsumer>
                {config.features?.use_old_kyc ?
                    <KycDetails
                        onSuccess={() => {
                            captureEvent("kyc_status_changed", { status: "APPROVED", flow, kyc_version: kycVersion });
                            refreshUser()
                        }
                        }
                        onException={() => {
                            window.location.reload();
                        }}
                        exceptionCTA={"Try again"}
                        stepsEnabled={false}
                        ocrEnabled={config.ocr_enabled}
                    />
                    :
                    <KycWrapper
                        presetKycCountry={presetKycCountry}
                        fixedPresetKycCountry={fixedPresetKycCountry}
                        user={user}
                        onStatusChange={onStatusChange}
                        onCompleted={() => { }}
                        externalDigilocker={externalDigilocker}
                    />
                }
            </AuthConsumer>
        )
    }

    return children
}